export const TOKEN_KEY = 'geodraft_token';

export interface Video360Rendition {
  name: string;
  label: string;
  width: number;
  height: number;
  bytes: number;
  url: string;
}

export type Video360Stereo = 'mono' | 'top-bottom' | 'left-right';

export interface Video360Delivery {
  status: 'ready' | 'processing' | 'error' | 'uploading';
  progress?: { stage?: string; pct?: number } | null;
  duration?: number;
  width?: number;
  height?: number;
  hasAudio?: boolean;
  stereo?: Video360Stereo;
  heading?: number;
  poster?: string;
  renditions?: Video360Rendition[];
}

export const formatDuration = (seconds: number): string => {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

// A 90° field of view shows a quarter of the sphere, so an equirect frame needs
// roughly three to four viewport widths of pixels before it looks sharp.
export const SHARPNESS_FACTOR = 3;

export const pickInitialRendition = <T extends { width: number }>(
  renditions: readonly T[],
  viewportWidthCssPx: number,
  devicePixelRatio: number,
): T | null => {
  if (renditions.length === 0) return null;
  const dpr = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
  const viewport = Number.isFinite(viewportWidthCssPx) && viewportWidthCssPx > 0 ? viewportWidthCssPx : 0;
  const target = viewport * dpr * SHARPNESS_FACTOR;
  const fitting = renditions.filter((r) => r.width <= target);
  if (fitting.length > 0) return fitting.reduce((best, r) => (r.width > best.width ? r : best));
  return renditions.reduce((smallest, r) => (r.width < smallest.width ? r : smallest));
};

export const renditionOptionLabel = (r: Pick<Video360Rendition, 'label' | 'width' | 'height'>): string =>
  `${r.label} · ${r.width}×${r.height}`;

export interface TextureCrop {
  repeat: [number, number];
  offset: [number, number];
}

// three.js samples v = 1 at the top row of the frame, so the top eye of a
// top-bottom pair lives in the upper half of UV space.
export const stereoEyeCrop = (stereo: string | null | undefined): TextureCrop | null => {
  if (stereo === 'top-bottom') return { repeat: [1, 0.5], offset: [0, 0.5] };
  if (stereo === 'left-right') return { repeat: [0.5, 1], offset: [0, 0] };
  return null;
};

const readStoredToken = (): string => {
  try {
    return (typeof localStorage !== 'undefined' && localStorage.getItem(TOKEN_KEY)) || '';
  } catch {
    return '';
  }
};

export interface DeliveryRequest {
  url: string;
  headers: Record<string, string> | undefined;
}

export const deliveryRequest = (fileId: string, shareToken?: string | null): DeliveryRequest => {
  const base = `/api/files/${encodeURIComponent(fileId)}/video360-url`;
  if (shareToken) {
    return { url: `${base}?shareToken=${encodeURIComponent(shareToken)}`, headers: undefined };
  }
  return { url: base, headers: { Authorization: `Bearer ${readStoredToken()}` } };
};
